import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method: string;
  userEmail?: string;
  userName?: string;
  invoiceId?: string;
  invoiceUrl?: string;
  createdAt: string;
  description?: string;
  paymentCountry?: string;
  paymentType?: 'seat' | 'subscription' | 'credit' | 'collaborator_seat' | 'execution_seat' | 'other';
}

interface BillingAnalytics {
  totalRevenue: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  averageOrderValue: number;
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  successRate: number;
  conversionRate: number;
  revenueGrowth: number;
  paymentMethodDistribution: Array<{ method: string; count: number; percentage: number }>;
  topPayingCountries: Array<{ country: string; revenue: number; percentage: number }>;
}

interface Subscription {
  id: string;
  planName: string;
  price: number;
  currency: string;
  interval: string;
  status: string;
  userEmail?: string;
  userName?: string;
  startDate: string;
  currentPeriodEnd: string;
  cancelAt?: string;
  quantity: number;
}

interface Account {
  id: string;
  accountName: string;
  userEmail?: string;
  userName?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  totalPaid: number;
  currency: string;
  createdAt: string;
  country?: string;
}

interface RevenueTrend {
  date: string;
  revenue: number;
  transactions: number;
}

interface SubscriptionMetrics {
  totalSubscriptions: number;
  activeSubscriptions: number;
  churned: number;
  newSubscriptions: number;
  monthlyRecurringRevenue: number;
  churnRate: number;
  lifetimeValue: number;
  totalSeats: number;
}

interface PaymentFailure {
  reason: string;
  count: number;
  percentage: number;
}

interface PaymentFilters {
  page?: number;
  limit?: number;
  status?: string;
  paymentType?: 'seat' | 'subscription' | 'credit' | 'collaborator_seat' | 'execution_seat' | 'other' | '';
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/billing',
    prepareHeaders: (headers, { getState }) => {
      // Try Redux store first, then localStorage as fallback
      const state = getState() as any;
      const token = state.auth?.token || localStorage.getItem('admin_token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Payment', 'Subscription', 'Account', 'Analytics'],
  endpoints: (builder) => ({
    // Get paginated payments with filters
    getPayments: builder.query<PaginatedResponse<Payment>, PaymentFilters>({
      query: (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
        return `/payments?${params.toString()}`;
      },
      providesTags: ['Payment'],
    }),
    
    // Get payment by ID
    getPaymentById: builder.query<Payment, number>({
      query: (id) => `/payments/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Payment', id }],
    }),
    
    // Get billing analytics
    getBillingAnalytics: builder.query<BillingAnalytics, void>({
      query: () => '/analytics',
      transformResponse: (response: ApiResponse<BillingAnalytics>) => response.data,
      providesTags: ['Analytics'],
    }),
    
    // Get all subscriptions
    getSubscriptions: builder.query<Subscription[], void>({
      query: () => '/subscriptions',
      transformResponse: (response: ApiResponse<Subscription[]>) => response.data,
      providesTags: ['Subscription'],
    }),
    
    // Get accounts with subscription details
    getAccounts: builder.query<PaginatedResponse<Account>, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 20 }) => `/accounts?page=${page}&limit=${limit}`,
      providesTags: ['Account'],
    }),
    
    // Get revenue trends
    getRevenueTrends: builder.query<RevenueTrend[], number>({
      query: (days = 30) => `/revenue-trends?days=${days}`,
      transformResponse: (response: ApiResponse<RevenueTrend[]>) => response.data,
      providesTags: ['Analytics'],
    }),
    
    // Get subscription metrics
    getSubscriptionMetrics: builder.query<SubscriptionMetrics, void>({
      query: () => '/subscription-metrics',
      transformResponse: (response: ApiResponse<SubscriptionMetrics>) => response.data,
      providesTags: ['Analytics'],
    }),
    
    // Get payment failure reasons
    getPaymentFailureReasons: builder.query<PaymentFailure[], void>({
      query: () => '/payment-failures',
      transformResponse: (response: ApiResponse<PaymentFailure[]>) => response.data,
      providesTags: ['Analytics'],
    }),
    
    // Get billing dashboard data
    getBillingDashboard: builder.query<any, void>({
      query: () => '/dashboard',
      providesTags: ['Analytics'],
    }),
    
    // Get payment statistics
    getPaymentStats: builder.query<any, void>({
      query: () => '/payment-stats',
      transformResponse: (response: ApiResponse<any>) => response.data,
      providesTags: ['Analytics'],
    }),
  }),
})

export const {
  useGetPaymentsQuery,
  useGetPaymentByIdQuery,
  useGetBillingAnalyticsQuery,
  useGetSubscriptionsQuery,
  useGetAccountsQuery,
  useGetRevenueTrendsQuery,
  useGetSubscriptionMetricsQuery,
  useGetPaymentFailureReasonsQuery,
  useGetBillingDashboardQuery,
  useGetPaymentStatsQuery,
} = billingApi 